import React, { useRef, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";

import ActionContainer from "../containers/actionContainer";
import styled from "@emotion/styled";
import { mediaPhone, mediaDesktop } from "../../styles/Breakpoints";
import Button from "../containers/button";
import { defaultAction } from "../../../actions/defaultAction";
import { SET_SELECTED_LOCATION } from "../../../constants/actionTypes";

const Container = styled.div`
  background-color: white;
  border-radius: 0.2rem;
  overflow: visible;
  display: flex;
  flex-direction: column;
  align-items: center;

  p {
    padding: 0 1rem 0 1rem;
    text-align: justify;
  }

  ${mediaDesktop} {
    margin: 0 auto;
    width: 100%;
  }

  ${mediaPhone} {
    overflow-y: auto;
  }
`;

const Actions = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 95px;
`;

const LocationContainer = styled.div`
  width: 100%;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  padding: 0 1rem;
  position: relative;
  z-index: 1;
  box-sizing: border-box;

  ${mediaPhone} {
    padding: 0 0.5rem;
    width: calc(100% - 1rem);
    max-width: 100%;
  }
`;

const Agreement = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const controlRef = useRef(null);
  const [controlWidth, setControlWidth] = useState("100%");
  
  const title = useSelector((state) => state.config.agreementTitle);
  const acceptOfferTxt = useSelector(
    (state) => state.config.agreementAcceptOfferTxt
  );
  const buttonTxt = useSelector(
    (state) => state.config.agreementContinueButtonTxt
  );
  const content = useSelector((state) => state.config.agreementContent);
  const locationPlaceholder = useSelector(
    (state) => state.config.agreementLocationPlaceholder
  );
  const locations = useSelector((state) => state.config.agreementLocations);
  const selectedLocation = useSelector(
    (state) => state.customerInfo.get("selectedLocation")
  );
  const primaryColor = useSelector((state) => state.config.theme.primary_color);

  const locationOptions = locations
    ? locations.map((location) => ({
        value: location,
        label: location,
      }))
    : [];

  const handleLocationChange = (selectedOption) => {
    dispatch(defaultAction(SET_SELECTED_LOCATION, selectedOption));
  };

  // Only require location selection if locations are configured
  const hasLocations = locations && locations.length > 0;
  const isButtonDisabled = hasLocations && !selectedLocation;

  useEffect(() => {
    const updateWidth = () => {
      if (controlRef.current) {
        const width = controlRef.current.offsetWidth;
        setControlWidth(`${width}px`);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  return (
    <ActionContainer title={title} goBack={true} scrollable={true}>
      <React.Fragment>
        <Container>
          <ul>
            {content.map((line, index) => (
              <li
                key={index}
                dangerouslySetInnerHTML={{
                  __html: line,
                }}
              />
            ))}
          </ul>
          <p>{acceptOfferTxt}</p>
          {locations && locations.length > 0 && (
            <LocationContainer>
              <div ref={controlRef} style={{ width: "100%" }}>
                <Select
                  placeholder={locationPlaceholder || "Select location"}
                  value={selectedLocation}
                  onChange={handleLocationChange}
                  options={locationOptions}
                  isClearable={false}
                  className="form-control"
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                styles={{
                  indicatorSeparator: (styles) => ({ ...styles, width: "0px" }),
                  control: (styles, { isFocused }) => ({
                    ...styles,
                    borderStyle: "none",
                    borderWidth: "0px",
                    borderRadius: "0px",
                    borderBottom: `1px solid ${primaryColor}`,
                    width: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                  }),
                  menuPortal: (styles) => ({
                    ...styles,
                    zIndex: 9999,
                  }),
                  menu: (styles) => ({
                    ...styles,
                    marginTop: "0px",
                    borderRadius: "0 0 30px 30px",
                    overflow: "hidden",
                    borderStyle: "none",
                    width: controlWidth,
                    maxWidth: "calc(100vw - 2rem)",
                    minWidth: "200px",
                    boxSizing: "border-box",
                  }),
                  menuList: (styles) => ({
                    ...styles,
                    maxHeight: "14rem",
                    overflowX: "hidden",
                  }),
                  option: (styles, { isSelected, isFocused }) => {
                    // Convert hex to rgba for opacity
                    const hexToRgba = (hex, alpha) => {
                      const r = parseInt(hex.slice(1, 3), 16);
                      const g = parseInt(hex.slice(3, 5), 16);
                      const b = parseInt(hex.slice(5, 7), 16);
                      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
                    };
                    
                    return {
                      ...styles,
                      backgroundColor: isSelected
                        ? hexToRgba(primaryColor, 0.1) // Light background for selected
                        : isFocused
                        ? hexToRgba(primaryColor, 0.05) // Very light for hover
                        : "#FFFFFF",
                      color: "#33433d", // Dark text for visibility
                      cursor: "pointer",
                      "&:active": {
                        backgroundColor: hexToRgba(primaryColor, 0.15),
                      },
                    };
                  },
                  placeholder: (styles) => ({
                    ...styles,
                    color: "#33433d",
                    opacity: "1",
                    fontSize: "1rem",
                  }),
                }}
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    primary25: "#FFFFFF",
                    primary: "#FFFFFF",
                  },
                })}
                />
              </div>
            </LocationContainer>
          )}
        </Container>
      </React.Fragment>
      <React.Fragment>
        <Actions>
          <Button
            onClick={() => {
              history.push("/card-details");
            }}
            active={!isButtonDisabled}
            disabled={isButtonDisabled}
          >
            {buttonTxt}
          </Button>
        </Actions>
      </React.Fragment>
    </ActionContainer>
  );
};

export default Agreement;
